import { AccountType } from '@tassat/common-definitions';
import { AccountRecord } from '@tassat/fred-definitions';
import { createSelector } from 'reselect';

import { AdminState } from 'Reducers/store/adminStore';
import { ClientState } from 'Reducers/store/clientStore';

import { getOpenWalletssByFirmId } from './accounts';
import { getAccountRelationships, getAccounts, getAccountsWithdrawalRelationships } from './common';
import { getWithdrawalInstructionsArray } from './withdrawalInstructions';

const getWalletAccountId = (_: AdminState, walletAccountId: string) => walletAccountId || '';
const getAccountWithdrawalDataId = (_: AdminState | ClientState, _firmId: string, accountWithdrawalDataId: string) => accountWithdrawalDataId || '';
const getFirmId = (_: AdminState | ClientState, firmId: string) => firmId || '';

export const getAccountWithdrawalRelationshipsByFirmId = createSelector(
    [ getAccountsWithdrawalRelationships, getFirmId ], (accountWithdrawalRelationshipsList, firmId) => {
        if (!accountWithdrawalRelationshipsList) {
            return [];
        }

        return Object.values(accountWithdrawalRelationshipsList)
            .flatMap(id => Object.values(id))
            .filter(relationship => relationship.FirmId === firmId);
    }
);

export const getLinkedAccountForWallet = createSelector(
    [ getAccountRelationships, getAccounts, getWalletAccountId ], (accountRelationshipsList, accounts, walletAccountId) => {
        const relatedAccounts = Object.keys(accountRelationshipsList)
            .map(id => accountRelationshipsList[id])
            .filter(relationship => relationship.AccountId2 === walletAccountId || relationship.AccountId1 === walletAccountId);

        return relatedAccounts.map(relation => {
            return Object.keys(accounts)
                .map(id => accounts[id])
                .find(account => (account.AccountId === relation.AccountId2 || account.AccountId === relation.AccountId1) &&
                    account.AccountTypeId === AccountType.CLIENT_BDA);
        });
    }
);

export const getAssociatedWalletsbyAccountWithdrawalDataId = createSelector(
    [ getAccountsWithdrawalRelationships, getWithdrawalInstructionsArray, getAccounts, getAccountWithdrawalDataId ],
    (accountWithdrawalRelationshipsList, withdrawalInstructions, accounts, accountWithdrawalDataId) => {
        const relatedWallets = Object.values(accountWithdrawalRelationshipsList)
            .flatMap(id => Object.values(id))
            .filter(relationship => withdrawalInstructions.find(
                instruction => instruction.AccountWithdrawalDataId === relationship.AccountWithdrawalDataId &&
                    instruction.AccountWithdrawalDataId === accountWithdrawalDataId && !relationship.IsDeleted
            ));

        return relatedWallets.map(relation => {
            return Object.keys(accounts)
                .map(id => accounts[id])
                .find(account => (account.AccountId === relation.AccountId) &&
                    account.AccountTypeId === AccountType.CLIENT_EOA);
        });
    }
);

export const getUnmappedWalletsByAccountWithdrawalDataId = createSelector(
    [ getOpenWalletssByFirmId, getAssociatedWalletsbyAccountWithdrawalDataId ],
    (openWallets, withdrawalAssociatedWallets): AccountRecord[] => {
        if (!withdrawalAssociatedWallets.length) {
            return openWallets;
        } else {
            return openWallets.filter((wallet) => !withdrawalAssociatedWallets
                .find((associatedWallet) => associatedWallet?.AccountId === wallet.AccountId));
        }
    }
);